import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, LightbulbIcon, MessageCircleIcon, HomeIcon, AnchorIcon, ShieldIcon, StarIcon, ArrowRightIcon, PhoneIcon, CheckCircleIcon } from 'lucide-react';
import { FadeIn } from '../components/FadeIn';
import { TrustBar } from '../components/TrustBar';
import { SectionHeading } from '../components/SectionHeading';
import { Button } from '../components/Button';
import { CTASection } from '../components/CTASection';
interface HomePageProps {
  onNavigate: (page: string) => void;
}
export function HomePage({
  onNavigate
}: HomePageProps) {
  return <div className="w-full overflow-hidden" data-id="element-352">
      {/* Hero Section */}
      <section className="relative pt-36 md:pt-44 pb-16 md:pb-24 bg-white" data-id="element-353">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-354">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-355">
            <div className="w-full md:w-1/2 z-10" data-id="element-356">
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-navy leading-tight mb-6 tracking-tight" data-id="element-357">
                Hoi, ik ben Jordy.
                <br data-id="element-358" />
                <span className="text-2xl md:text-3xl lg:text-4xl font-bold text-gold mt-2 block" data-id="element-359">
                  Jouw persoonlijke financieel adviseur.
                </span>
              </motion.h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed max-w-lg" data-id="element-360">
                Hypotheekadvies en verzekeringen in Badhoevedorp, Amsterdam en
                Hoofddorp. Ik zoek net zolang door tot we de beste oplossing
                voor jouw situatie hebben gevonden.
              </motion.p>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="flex flex-col sm:flex-row gap-4" data-id="element-361">
                <Button variant="primary" size="lg" onClick={() => onNavigate('contact')} data-id="element-362">
                  Plan een gratis kennismaking
                </Button>
                <Button variant="secondary" size="lg" href="tel:0201234567" icon={<PhoneIcon className="w-5 h-5" data-id="element-364" />} iconPosition="left" data-id="element-363">
                  Bel direct
                </Button>
              </motion.div>
              <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.4
            }} className="text-sm text-gray-500 mt-6 flex items-center gap-2 font-medium" data-id="element-365">
                <CheckCircleIcon className="w-4 h-4 text-gold" data-id="element-366" />
                Eerste gesprek is altijd gratis en vrijblijvend
              </motion.p>
            </div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="w-full md:w-1/2 relative h-[400px] md:h-[600px] rounded-2xl overflow-hidden shadow-card-lg" data-id="element-367">
              <img src="https://cdn.magicpatterns.com/uploads/8YqgEQJ5jSY63NDsDuuJzL/5A2A7661-bewerkt-3.jpg" alt="Jordy de Gier — Financieel Adviseur" className="absolute inset-0 w-full h-full object-cover object-top" data-id="element-368" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent" data-id="element-369" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Indicators Bar */}
      <TrustBar data-id="element-370" />

      {/* Personal Intro Section */}
      <section className="py-20 md:py-28 bg-warm" data-id="element-371">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-372">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-373">
            <FadeIn className="w-full md:w-1/3" data-id="element-374">
              <img src="https://cdn.magicpatterns.com/uploads/xuQDP4soQA1T2E7yMeP3Fj/5A2A7557-bewerkt-3.jpg" alt="Jordy de Gier" loading="lazy" className="rounded-2xl shadow-card-lg aspect-square object-cover w-full max-w-sm mx-auto" data-id="element-375" />
            </FadeIn>
            <FadeIn delay={0.2} className="w-full md:w-2/3" data-id="element-376">
              <h4 className="text-gold font-bold tracking-wider uppercase mb-2" data-id="element-377">
                Over Jordy
              </h4>
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-6" data-id="element-378">
                Ik kijk verder dan de standaard regeltjes
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-379">
                Een hypotheek of verzekering is geen formuliertje — het gaat om
                de belangrijkste beslissingen in je leven. Daarom krijg je bij
                mij geen keuzemenu of wisselende contactpersonen. Je hebt mijn
                mobiele nummer, ik reageer via WhatsApp, en als een bank 'nee'
                zegt, zoek ik net zolang door tot we wél een oplossing vinden.
              </p>
              <ul className="space-y-3 mb-8" data-id="element-380">
                <li className="flex items-center text-navy font-medium" data-id="element-381">
                  <CheckCircleIcon className="w-5 h-5 text-gold mr-3" data-id="element-382" /> 15+
                  jaar ervaring in de financiële sector
                </li>
                <li className="flex items-center text-navy font-medium" data-id="element-383">
                  <CheckCircleIcon className="w-5 h-5 text-gold mr-3" data-id="element-384" /> Erkend
                  Financieel Adviseur (SEH)
                </li>
                <li className="flex items-center text-navy font-medium" data-id="element-385">
                  <CheckCircleIcon className="w-5 h-5 text-gold mr-3" data-id="element-386" />{' '}
                  Bereikbaar via WhatsApp, ook 's avonds
                </li>
              </ul>
              <Button variant="ghost" onClick={() => onNavigate('over-ons')} data-id="element-387">
                Lees meer over mij
              </Button>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* USP Section */}
      <section className="py-20 md:py-28 bg-white" data-id="element-388">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-389">
          <SectionHeading title="Waarom klanten bij mij terugkomen" subtitle="Veel van mijn klanten bellen me jaren later nog steeds. Niet omdat het moet, maar omdat ze weten dat ik meedenk." data-id="element-390" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10" data-id="element-391">
            <FadeIn delay={0.1} className="bg-warm p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-392">
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-6 shadow-sm" data-id="element-393">
                <ClockIcon className="w-7 h-7 text-gold" data-id="element-394" />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-3" data-id="element-395">
                Snel Schakelen
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-396">
                Geen lange wachttijden. Een afspraak is vaak dezelfde dag nog
                mogelijk, zodat je direct weet waar je aan toe bent.
              </p>
            </FadeIn>

            <FadeIn delay={0.2} className="bg-warm p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-397">
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-6 shadow-sm" data-id="element-398">
                <LightbulbIcon className="w-7 h-7 text-gold" data-id="element-399" />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-3" data-id="element-400">
                Creatieve Oplossingen
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-401">
                Waar standaard banken 'nee' zeggen, zoek ik net zolang door tot
                we toch een passende oplossing vinden.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} className="bg-warm p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-402">
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-6 shadow-sm" data-id="element-403">
                <MessageCircleIcon className="w-7 h-7 text-gold" data-id="element-404" />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-3" data-id="element-405">
                Persoonlijk Contact
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-406">
                Korte lijntjes en direct contact via WhatsApp met je eigen
                adviseur. Geen keuzemenu's of wisselende contactpersonen.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="py-20 md:py-28 bg-warm" data-id="element-407">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-408">
          <SectionHeading title="Waar ik je mee help" subtitle="Van je eerste koophuis tot een woonboot op de Amstel. En als er iets misgaat, bel je mij." data-id="element-409" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-410">
            {/* Pillar 1: Hypotheken */}
            <FadeIn delay={0.1} className="flex flex-col bg-white rounded-2xl p-8 border border-gray-100 shadow-card hover:shadow-card-hover transition-all duration-300 group" data-id="element-411">
              <HomeIcon className="w-12 h-12 text-navy mb-6" strokeWidth={1.5} data-id="element-412" />
              <h3 className="text-2xl font-bold text-navy mb-4" data-id="element-413">Hypotheken</h3>
              <p className="text-gray-600 mb-8 flex-grow" data-id="element-414">
                Voor starters, doorstromers, ondernemers en bij scheidingen. Ik
                vergelijk de voorwaarden van meer dan 30 geldverstrekkers en
                zoek de beste deal voor jouw situatie.
              </p>
              <button onClick={() => onNavigate('hypotheken')} className="flex items-center text-gold font-bold group-hover:text-navy transition-colors" data-id="element-415">
                Lees meer{' '}
                <ArrowRightIcon className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-416" />
              </button>
            </FadeIn>

            {/* Pillar 2: Woonboten */}
            <FadeIn delay={0.2} className="flex flex-col bg-white rounded-2xl p-8 border border-gray-100 shadow-card hover:shadow-card-hover transition-all duration-300 group" data-id="element-417">
              <AnchorIcon className="w-12 h-12 text-navy mb-6" strokeWidth={1.5} data-id="element-418" />
              <h3 className="text-2xl font-bold text-navy mb-4" data-id="element-419">Woonboten</h3>
              <p className="text-gray-600 mb-8 flex-grow" data-id="element-420">
                De meeste banken zeggen 'nee' tegen woonboten. Ik heb het
                netwerk van gespecialiseerde geldverstrekkers die wél
                financieren. Van ligplaatsvergunning tot notaris — ik regel het.
              </p>
              <button onClick={() => onNavigate('woonboten')} className="flex items-center text-gold font-bold group-hover:text-navy transition-colors" data-id="element-421">
                Lees meer{' '}
                <ArrowRightIcon className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-422" />
              </button>
            </FadeIn>

            {/* Pillar 3: Verzekeringen */}
            <FadeIn delay={0.3} className="flex flex-col bg-navy rounded-2xl p-8 shadow-card hover:shadow-card-hover transition-all duration-300 relative overflow-hidden" data-id="element-423">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-bl-full" data-id="element-424" />
              <ShieldIcon className="w-12 h-12 text-gold mb-6 relative z-10" strokeWidth={1.5} data-id="element-425" />
              <h3 className="text-2xl font-bold text-white mb-4 relative z-10" data-id="element-426">
                Verzekeringen
              </h3>
              <p className="text-gray-300 mb-8 flex-grow relative z-10" data-id="element-427">
                Wat als er iets gebeurt? Dan bel je mij. Ik regel de
                schadeafhandeling en zorg dat je goed verzekerd bent — niet
                meer, niet minder.
              </p>
              <div className="flex flex-col gap-3 relative z-10 mt-auto" data-id="element-428">
                <Button variant="primary" href="tel:0201234567" className="w-full" data-id="element-429">
                  Schade melden
                </Button>
                <button onClick={() => onNavigate('verzekeringen')} className="text-white hover:text-gold font-medium transition-colors text-center py-2" data-id="element-430">
                  Bekijk verzekeringen
                </button>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Trust / Reviews Section */}
      <section className="py-20 md:py-28 bg-white relative overflow-hidden" data-id="element-431">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[400px] text-navy/5 font-serif leading-none select-none pointer-events-none" data-id="element-432">
          "
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10" data-id="element-433">
          <SectionHeading title="Wat onze klanten zeggen" data-id="element-434" />

          <div className="flex overflow-x-auto md:grid md:grid-cols-2 gap-6 pb-8 md:pb-0 hide-scrollbar snap-x snap-mandatory" data-id="element-435">
            <div className="min-w-[300px] bg-warm p-8 rounded-2xl shadow-card snap-center flex-shrink-0 border border-gray-100" data-id="element-436">
              <div className="flex gap-1 mb-4" data-id="element-437">
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-438" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-439" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-440" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-441" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-442" />
              </div>
              <p className="text-gray-700 italic mb-6 leading-relaxed" data-id="element-443">
                "Jordy heeft ons fantastisch geholpen bij onze eerste woning.
                Snel, duidelijk en altijd bereikbaar via WhatsApp. Geen vraag
                was te veel."
              </p>
              <p className="font-bold text-navy" data-id="element-444">Sander & Lisa</p>
            </div>

            <div className="min-w-[300px] bg-warm p-8 rounded-2xl shadow-card snap-center flex-shrink-0 border border-gray-100" data-id="element-445">
              <div className="flex gap-1 mb-4" data-id="element-446">
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-447" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-448" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-449" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-450" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-451" />
              </div>
              <p className="text-gray-700 italic mb-6 leading-relaxed" data-id="element-452">
                "Als ZZP'er dacht ik dat een hypotheek lastig zou worden. Jordy
                dacht out-of-the-box en had binnen een week een akkoord bij een
                bank."
              </p>
              <p className="font-bold text-navy" data-id="element-453">Mark (Ondernemer)</p>
            </div>

            <div className="min-w-[300px] bg-warm p-8 rounded-2xl shadow-card snap-center flex-shrink-0 border border-gray-100" data-id="element-454">
              <div className="flex gap-1 mb-4" data-id="element-455">
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-456" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-457" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-458" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-459" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-460" />
              </div>
              <p className="text-gray-700 italic mb-6 leading-relaxed" data-id="element-461">
                "Voor de financiering van onze woonboot kregen we overal 'nee'
                te horen, totdat we bij JDG kwamen. Zeer deskundig in deze
                niche!"
              </p>
              <p className="font-bold text-navy" data-id="element-462">Familie de Vries</p>
            </div>

            <div className="min-w-[300px] bg-warm p-8 rounded-2xl shadow-card snap-center flex-shrink-0 border border-gray-100" data-id="element-463">
              <div className="flex gap-1 mb-4" data-id="element-464">
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-465" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-466" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-467" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-468" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-469" />
              </div>
              <p className="text-gray-700 italic mb-6 leading-relaxed" data-id="element-470">
                "Na mijn scheiding zat ik met veel onzekerheid. Jordy bracht
                rust en overzicht, en zorgde dat ik in het huis kon blijven
                wonen."
              </p>
              <p className="font-bold text-navy" data-id="element-471">Emma</p>
            </div>
          </div>

          <FadeIn delay={0.2} className="text-center mt-12" data-id="element-472">
            <a href="#" className="inline-flex items-center gap-3 text-navy hover:text-gold font-bold transition-colors bg-white px-8 py-4 rounded-full shadow-md border border-gray-100 hover:border-gold" data-id="element-473">
              <div className="bg-white p-1 rounded-full shadow-sm" data-id="element-474">
                <svg className="w-6 h-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-475">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" data-id="element-476" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-477" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-478" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-479" />
                </svg>
              </div>
              Bekijk alle reviews op Google (4.9/5)
            </a>
          </FadeIn>
        </div>
      </section>

      {/* Final CTA */}
      <CTASection title="Bel me gerust voor een eerste check" buttonText="Plan een koffie" onNavigate={onNavigate} page="contact" variant="dark" data-id="element-480" />
    </div>;
}